import { t } from "i18next";
import { DragDropEvent } from "services/dragdrop/DragDropContext";
import { SchedulerDragEventDetails, SchedulerDraggableId, SchedulerDroppableId, SchedulingJobType } from "../SchedulingTypes";
import { formatDisplayDate } from "internationalization/timezoneConversions";
import { getFirstDateOfWeek } from "./jobInboxUtils";
import { saveData } from "services/api/saveData";

export const handleJobInboxDrag = async (
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId>,
    pastJobs: SchedulingJobType[],
    untargetedJobs: SchedulingJobType[],
    weekJobs: { [week: string]: SchedulingJobType[] },
    weekStartsOnSunday: boolean,
    userLocale: string,
    setFloatingAlert: Function,
    setPastJobs: React.Dispatch<React.SetStateAction<SchedulingJobType[]>>,
    setUntargetedJobs: React.Dispatch<React.SetStateAction<SchedulingJobType[]>>,
    setWeekJobs: React.Dispatch<React.SetStateAction<{ [week: string]: SchedulingJobType[] }>>,
) => {
    // Get the values from the drag result
    const { source, destination, draggableId } = dragResult;

    if (!destination) return;

    // Get the source type, destination type and draggable type
    const sourceType = source?.droppableId?.type
    const destinationType = destination.droppableId.type;
    const draggableType = draggableId.type;

    // Get the values from the completed drag event
    const { sourceSection, sourceWeekKey, destinationSection, destinationWeekKey, draggableItemType, draggableJob } = extractDragEventDetails(dragResult);

    // Prevent users from moving jobs from a week row to a past jobs row
    if (destinationSection === 'pastJobs' && sourceSection !== 'pastJobs') {
        setFloatingAlert({ 
            type: 'danger',
            message: t("scheduling.job_inbox.invalid_move_to_past_alert")
        });
        return;
    }

    // Handle dragging of inbox jobs to the untargeted jobs section
    if (draggableItemType === 'jobInbox' && destinationSection === 'untargetedJobs') {

        // Skip if a job is dropped back into the same section it was taken from
        if (sourceSection === destinationSection) {
            return;
        }

        // Get the right list of jobs wheter the item is dragged from the past- or weekjobs
        const initialJobs = 
            sourceSection === 'pastJobs' ? [...pastJobs]
            : sourceSection === 'weekJobs' && sourceWeekKey ? [...(weekJobs[sourceWeekKey] || [])]
            : [];

        // Find the index of the moved job
        const jobIndex = initialJobs.findIndex(job => job.id === draggableJob?.id);

        // Continue if the index is found
        if (jobIndex > -1) {
            const [movedJob] = initialJobs.splice(jobIndex, 1);

            // Remove the moved job from either the source section
            if (sourceSection === 'pastJobs') {
                setPastJobs(initialJobs);
            } else {
                if (sourceWeekKey) {
                    setWeekJobs(prev => ({ ...prev, [sourceWeekKey]: initialJobs }));
                }
            }

            // Add the moved job to the untargeted jobs state
            setUntargetedJobs(prev => ({ ...prev, movedJob }));

            // Save the updated job details, remove the target date
            const response = await saveData({
                apiObject: 'job',
                itemId: movedJob.id,
                data: { target_date: null }
            });

            // Show floating alert on success
            if (response?.status === 200) {
                setFloatingAlert({ 
                    type: 'success',
                    message: t("scheduling.job_inbox.moved_to_untargeted_jobs") 
                });
            }
        }
    }

    // Handle dragging of inbox jobs to other week rows inside the job inbox
    if (draggableItemType === 'jobInbox' && destinationSection === 'weekJobs' && destinationWeekKey) {
    
        // Skip if a job is dropped back into the same week it was taken from
        if (sourceWeekKey === destinationWeekKey) {
            return;
        }
        
        // Set the dragging item to null to prevent flickering when updating the past- or weekjobs states
        // setDraggingItem(null);

        // Get the right list of jobs wheter the item is dragged from the past- or weekjobs
        const initialJobs = 
            sourceSection === 'pastJobs' ? [...pastJobs]
            : sourceSection === 'untargetedJobs' ? [...untargetedJobs]
            : sourceSection === 'weekJobs' && sourceWeekKey ? [...(weekJobs[sourceWeekKey] || [])]
            : [];

        // Find the index of the moved job
        const jobIndex = initialJobs.findIndex(job => job.id === draggableJob?.id);

        // Continue if the index is found
        if (jobIndex > -1) {
            const [movedJob] = initialJobs.splice(jobIndex, 1);

            // Add the moved jobs to the destination week
            const destinationJobs = [...(weekJobs[destinationWeekKey] || [])];
            destinationJobs.push(movedJob);

            // Remove the moved job from either the source section
            if (sourceSection === 'pastJobs') {
                setPastJobs(initialJobs);

            } else if (sourceSection === 'untargetedJobs') {
                setUntargetedJobs(initialJobs);

            } else {
                if (sourceWeekKey) {
                    setWeekJobs(prev => ({ ...prev, [sourceWeekKey]: initialJobs }));
                }
            }

            // Update the weekjobs state to add the job to the new destination week
            setWeekJobs(prev => ({ ...prev, [destinationWeekKey]: destinationJobs }));

            // Adjust the new target date to the same weekday
            const originalTargetDate = new Date(movedJob.target_date!);
            const originalTargetDateDay = originalTargetDate.getDay();

            // If the original target date day is on sunday (day 0), replace it with 7, to put it on the end of the week instead of the beginning
            const originalTargetDateDayOffset = (originalTargetDateDay === 0 ? 7 : originalTargetDateDay);
            
            // Calculate the new target date
            const firstDayOfWeek = getFirstDateOfWeek([destinationWeekKey]);
            const newTargetDate = new Date(firstDayOfWeek);

            // Add the original target date day offset
            newTargetDate.setDate(newTargetDate.getDate() + originalTargetDateDayOffset - (weekStartsOnSunday ? -1 : 0))

            // Convert the new target date to an iso string
            const formattedNewTargetDate = newTargetDate.toISOString().split('T')[0]

            // Save the updated job details
            const response = await saveData({
                apiObject: 'job',
                itemId: movedJob.id,
                data: { target_date: formattedNewTargetDate }
            });

            // Get the week number from the destination week
            const weekNumber = parseInt(destinationWeekKey.split('-')[1], 10);

            // Show floating alert on success
            if (response?.status === 200) {
                setFloatingAlert({ 
                    type: 'success',
                    message: t("scheduling.job_inbox.target_date_changed_alert", {
                        target_date: formatDisplayDate(new Date(formattedNewTargetDate), userLocale), 
                        week_number: weekNumber
                    }) 
                });
            }
        }                    
    };

    // Handle removing of a job inbox job after dragging it into a day or timeslot
    if (draggableItemType === 'jobInbox') {

        if (sourceType === 'jobInbox' && draggableType === 'jobInbox' && destinationType === 'timeSlot') {

            // Get the source week key
            const sourceWeekKey = source?.droppableId?.weekKey;

            // Get the dragged job id
            const draggedJobId = draggableId.job.id;

            // Remove the dragged job from either the week jobs or past jobs state
            if (source.droppableId?.section === 'weekJobs' && sourceWeekKey) {
                setWeekJobs(prev => {
                    const updatedWeekJobs = { ...prev };
                    Object.keys(updatedWeekJobs).forEach(weekKey => {
                        updatedWeekJobs[weekKey] = updatedWeekJobs[weekKey].filter(job => job.id !== draggedJobId);
                    });
                    return updatedWeekJobs;
                });    
            } 

            // Remove the dragged job from the untargeted jobs
            else if (source.droppableId?.section === 'untargetedJobs') {
                setUntargetedJobs(prev => prev.filter(job => job.id !== draggedJobId))
            }
            
            // Remove the dragged job from the past jobs
            else if (source.droppableId?.section === 'pastJobs') {
                setPastJobs(prev => prev.filter(job => job.id !== draggedJobId))
            }
        }
    }

    // Handle dragging of an appointment into the job inbox
    if (draggableItemType === 'event' && destinationSection === 'weekJobs' && destinationWeekKey) {
        // To implement in a later stage
    }
}

// Extract the details of a completed drag event
const extractDragEventDetails = (dragEvent: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId>): SchedulerDragEventDetails => {
    // Get the values from the drag event
    const { source, destination, draggableId } = dragEvent;

    // Source values
    const sourceType = source?.droppableId?.type === 'jobInbox' ? source?.droppableId?.type : undefined;
    const sourceSection = source?.droppableId?.type === 'jobInbox' ? source?.droppableId?.section : undefined;
    const sourceWeekKey = source?.droppableId?.type === 'jobInbox' && source?.droppableId.weekKey ? source?.droppableId?.weekKey : undefined;

    // Destination values
    const destinationSection = destination?.droppableId?.type === 'jobInbox' ? destination?.droppableId?.section : undefined;
    const destinationWeekKey = destination?.droppableId?.type === 'jobInbox' ? destination?.droppableId?.weekKey : undefined;

    // Draggable item values
    const draggableItemType = draggableId.type ==='jobInbox' ? draggableId.type : undefined;
    const draggableJob = draggableId.type === 'jobInbox' ? draggableId.job : undefined;
    
    return { sourceType, sourceSection, sourceWeekKey, destinationSection, destinationWeekKey, draggableItemType, draggableJob };
};